import React, { useMemo, useEffect } from 'react';
import { softwareBacklogConfigProviderEntrypoint } from '@jira/ai-rovo_actions-software-backlog/src/softwareBacklogConfigProviderEntrypoint.tsx';
import { softwareVersionDetailConfigProviderEntrypoint } from '@jira/ai-rovo_actions-software-version-detail/src/softwareVersionDetailConfigProviderEntrypoint.tsx';
import { projectVersionsConfigProviderEntrypoint } from '@jira/ai-rovo_actions-project-versions/src/projectVersionsConfigProviderEntrypoint.tsx';
import { issueConfigProviderEntrypoint } from '@jira/ai-rovo_actions-issue/src/issueConfigProviderEntrypoint.tsx';
import { useAiRovoCurrentRouteConfigurationStoreActions } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';
import type { RouteName } from '@atlassian/jira-route-name-types/src/RouteName.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router/src/controllers/router-store';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';
import { CONVERSATION_ASSISTANT_CONTAINER_ID } from '@atlassian/conversation-assistant/utils';
import { useActiveRightSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';

const useCurrentRouteName = createRouterSelector((state) => state.route.name);

/**
 * Add new entrypoints here to the type and to the jiraRouteToConfigProvider object
 */
type RovoEntryPointComponent =
	| typeof softwareBacklogConfigProviderEntrypoint
	| typeof softwareVersionDetailConfigProviderEntrypoint
	| typeof projectVersionsConfigProviderEntrypoint
	| typeof issueConfigProviderEntrypoint;

const jiraRouteToConfigProvider: Partial<Record<RouteName, RovoEntryPointComponent>> = {
	'software-backlog': softwareBacklogConfigProviderEntrypoint,
	'software-version-detail': softwareVersionDetailConfigProviderEntrypoint,
	'project-versions': projectVersionsConfigProviderEntrypoint,
	issue: issueConfigProviderEntrypoint,
};

const entryPointParams = {};

export const AiRovoCurrentRouteConfigurationProvider = () => {
	const routeName = useCurrentRouteName();
	const { resetConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();

	const entrypoint = useMemo(() => {
		if (routeName in jiraRouteToConfigProvider && jiraRouteToConfigProvider[routeName]) {
			return jiraRouteToConfigProvider[routeName];
		}
		return null;
	}, [routeName]);

	useEffect(() => {
		if (!entrypoint) {
			resetConfiguration();
		}
	}, [resetConfiguration, entrypoint]);

	if (!entrypoint) {
		return null;
	}

	return <RovoEntryPointContainer entrypoint={entrypoint} />;
};

const RovoEntryPointContainer = ({ entrypoint }: { entrypoint: RovoEntryPointComponent }) => {
	// eslint-disable-next-line @atlassian/react-entrypoint/prefer-entrypoint-file-import
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		entrypoint,
		entryPointParams,
	);
	const { setConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();
	const runtimeProps = useMemo(() => ({ setConfiguration }), [setConfiguration]);
	const { mode } = useRovoExperienceAPIStore();

	// TODO this shouldn't be needed since mode should be set to 'sidebar' when the sidebar is open,
	// but it isn't working right now. We should fix this in the rovo experience api store.
	const { panelId, isCollapsed } = useActiveRightSidebarState() || {};
	const isActive = panelId === CONVERSATION_ASSISTANT_CONTAINER_ID && !isCollapsed;

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (mode === 'mini-modal' || mode === 'sidebar' || isActive) {
			entryPointActions.load();
		}
	}, [entryPointActions, mode, isActive]);

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="jwm.rovo.current-route-configuration"
			packageName="jiraAiRovoActionsRouteProvider"
			runtimeProps={runtimeProps}
		/>
	);
};
